import React, { useContext, useEffect, useRef } from "react";
import { Grid, Container, Typography, Divider } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Link, Navigate, Route, Routes } from "react-router-dom";
import Overview from "./MyAccount/Overview";
import AutorenewOff from "./MyAccount/AutoRenewOff";
import AutorenewOn from "./MyAccount/AutoRenewOn";
import ChangeName from "./MyAccount/ChangeName";
import ChangeEmail from "./MyAccount/ChangeEmail";
import ChangePassword from "./MyAccount/ChangePassword";
import AddPhone from "./MyAccount/AddPhone";
import ChangePhone from "./MyAccount/ChangePhone";
import PaymentHistory from "./MyAccount/PaymentHistory";
import EditProfile from "./MyAccount/EditProfile";
import AddPaymentMethodAutoRenew from "./MyAccount/AddPaymentMethodAutoRenew";
import PaymentMethod from "./MyAccount/PaymentMethodAction";
import JoinNow from "./MyAccount/JoinNow";
import HeaderInternal from "./Header/HeaderInternal";
import Footer from "./Footer/Footer";
import MembershipCTABanner from "./MyAccount/MembershipCTABanner";
import GetAppCard, { GET_APP_CARD_STATUSES } from "./MyAccount/GetAppCard";
import { OverviewContext } from "../store/OverviewContext";
import { AccountContext } from "../store/AccountContext";
import { useNavigate, useLocation } from "react-router-dom";
import TrialSubscriptionWindow from "./MyAccount/TrialSubscriptionWindow";
import GracePeriodBanner from "./Banners/GracePeriod";
import AnnualUpgrade from "./MyAccount/AnnualUpgrade";
import { MEMBER_STATUS_STANDARD } from "@booyaltd/core";
import PriceRise from "./Banners/PriceRise";
import dayjs from "dayjs";

/**
 * PM5 T2 (money path, batch "myaccount-profile") — converted from MUI v4 +
 * `makeStyles` to MUI v5+ (`@mui/material`) `styled`/`sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * **SUBSCRIPTIONS/MEMBERSHIP ESCALATION CLASS.** This is the shell that routes
 * every My Account screen (auto-renew on/off, payment method, join now). The
 * conversion is styling-only: no route, guard, redirect, context read or
 * condition moved. Acceptance bar is visual parity.
 *
 * DEAD RULES DROPPED (guide §1.2 row 1): `myAccountSubTitle`,
 * `priceAlertBanner`, `priceAlertBannerWrapper`, `priceAlertBannerIcon`,
 * `priceInfoBanner`, `priceInfoBannerWrapper` and `priceInfoBannerIcon` were
 * declared in the v4 `useStyles` object but never referenced from the JSX, so
 * no element ever carried them and they rendered nothing. The two `*Wrapper`
 * rules are also where this file's only `.Mui*` descendant selectors lived
 * (`.MuiSvgIcon-root`, `.MuiAlertTitle-root`, `.MuiAlert-message` — the guide
 * §1.2b class) — dead in v4 and dead now, so there is nothing to re-point at
 * the `FfV5` names. Carrying them forward would be inventing styles.
 */

// was: useStyles().background — a bare <div>, which has no `sx` prop.
const PageBackground = styled("div")({
  flexGrow: 1,
  backgroundColor: "#e8e8e8",
});

// was: useStyles().myAccountContainer
const myAccountContainerSx = {
  paddingTop: "2%",
  paddingBottom: "5%",
};

// #1259: the board's space round the get-the-app card, above and below it
// (16px on a phone, 24px from `sm` up).
const getAppCardContainerSx = {
  paddingTop: { xs: 2, sm: 3 },
  paddingBottom: { xs: 2, sm: 3 },
};

// was: useStyles().myAccountGridContainer
const myAccountGridContainerSx = (theme) => ({
  padding: theme.spacing(2, 0),
});

// was: useStyles().myAccountTitle. The literal `@media (min-width:600px)` /
// `(min-width:960px)` queries become `breakpoints.up("sm"|"md")`; `themeV5`
// pins sm/md to the v4 values (600/960), so the emitted CSS is identical.
const myAccountTitleSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "2.2rem",
  textAlign: "left",
  // marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    fontSize: "2.2rem",
    lineHeight: "2.4rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "2.6rem",
    lineHeight: "2.8rem",
  },
  "& a": {
    color: theme.palette.background.ffNavy,
    textDecoration: "none",
  },
});

// was: useStyles().sectionDivider
const sectionDividerSx = {
  height: "1px",
  backgroundColor: "#c1c1c1",
};

// was: useStyles().joinNowTitle
const joinNowTitleSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "2.2rem",
  textAlign: "center",
  // marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    fontSize: "2.2rem",
    lineHeight: "2.4rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "2.6rem",
    lineHeight: "2.8rem",
  },
  "& a": {
    color: theme.palette.background.ffNavy,
    textDecoration: "none",
  },
});

export default function MyAccount() {
  const navigate = useNavigate();
  const { loading, loggedIn, userProfile } = useContext(AccountContext);
  const { overview } = useContext(OverviewContext);
  const location = useLocation();

  // Redirecting is a side effect — render-body pushes double-fire under
  // StrictMode's dev double-render; the ref keeps the push once-only (#637).
  const shouldRedirectToLogin = !loading && !loggedIn;
  const redirectedToLogin = useRef(false);
  useEffect(() => {
    if (shouldRedirectToLogin && !redirectedToLogin.current) {
      redirectedToLogin.current = true;
      navigate("/login");
    }
  }, [shouldRedirectToLogin, navigate]);

  if (shouldRedirectToLogin) {
    return null;
  }

  const isPaymentRetrying = userProfile
    ? userProfile.memberStatusId === "standard-expired" &&
      userProfile.productAutoRenewing
    : false;

  return (
    <>
      <HeaderInternal />
      <PageBackground>
        {/* Display CTA Upgrade Banner on Price Rise to everyone - Lock in Price Rise */}
        {overview &&
          userProfile &&
          location.pathname === "/myaccount/overview" &&
          overview.doors.annualUpgradeOpen &&
          userProfile.memberStatusId !== MEMBER_STATUS_STANDARD && (
            <PriceRise
              disableButton
              title="Beat the Price Rise"
              subTitle="Lock in the current price before they increase on 5th December 2022"
              showCountdown
            />
          )}

        {/* Display CTA Upgrade Banner on Price Rise to Standard Users with AR Disabled - Turn on Auto Renew to secure */}
        {overview &&
          userProfile &&
          location.pathname === "/myaccount/overview" &&
          overview.doors.annualUpgradeOpen &&
          userProfile.memberStatusId === MEMBER_STATUS_STANDARD &&
          dayjs(userProfile?.dateWebFirstLoggedIn).isAfter(
            dayjs("2022-11-25"),
          ) &&
          !userProfile.productAutoRenewing && (
            <PriceRise
              disableButton
              title="Beat the Price Rise"
              subTitle="Turn On Auto Renew to secure current price before the increase on 5th December 2022"
              showCountdown
            />
          )}

        {overview &&
          userProfile &&
          location.pathname === "/myaccount/overview" &&
          overview.doors.annualUpgradeOpen &&
          userProfile.memberStatusId === MEMBER_STATUS_STANDARD &&
          dayjs(userProfile?.dateWebFirstLoggedIn).isBefore(
            dayjs("2022-11-25"),
          ) &&
          userProfile.productAutoRenewing && (
            <PriceRise
              disableButton
              title="Our Price Rise will not affect you!"
              subTitle="Good News! As an active member, your price will remain on your current plan."
              showCountdown
            />
          )}

        {/* #1259: get-the-app card for a member who isn't paying, above the
            join banner; a trial member gets it with the trial line (#1261).
            Paying members get nothing here, not even the space round it.
            The website-trial card ("No smartphone or tablet?", #1261) now
            sits in Overview, under the profile table (Ellaie, 4 Nov). */}
        {userProfile &&
          location.pathname === "/myaccount/overview" &&
          GET_APP_CARD_STATUSES.includes(userProfile.memberStatusId) && (
            <Container sx={getAppCardContainerSx}>
              <GetAppCard memberStatusId={userProfile.memberStatusId} />
            </Container>
          )}

        {/* Display CTA Banner on Doors Open to everyone != standard/trial/standard-expired+autorenew */}
        {overview &&
          userProfile &&
          overview.doors.open &&
          location.pathname === "/myaccount/overview" &&
          !isPaymentRetrying &&
          userProfile.memberStatusId !== "standard" &&
          userProfile.memberStatusId !== "trial" && <MembershipCTABanner />}

        {/* Display Grace Period Banner based on first/second failed retry attempts */}
        {overview &&
          userProfile &&
          userProfile.memberStatusId === "standard" &&
          userProfile.subscriptionPaymentRetryCount > 0 && (
            <GracePeriodBanner />
          )}

        {/* Display 24H Countdown Banner when Trial Expired resurfaces - 72hr window to purchase */}
        {overview &&
          userProfile &&
          !overview.doors.open &&
          location.pathname === "/myaccount/overview" &&
          userProfile.memberStatusId === "trial-expired" && (
            <TrialSubscriptionWindow />
          )}
        <Container sx={myAccountContainerSx}>
          <>
            <Grid container sx={myAccountGridContainerSx}>
              {location.pathname !== "/myaccount/join-now" ? (
                <Grid size={{ xs: 12, sm: 12 }}>
                  <Typography variant="h1" sx={myAccountTitleSx}>
                    <Link to="/myaccount/overview">My Account</Link>
                  </Typography>
                  <Divider sx={sectionDividerSx} />
                </Grid>
              ) : (
                <Grid size={{ xs: 12, sm: 12 }}>
                  <Typography variant="h1" sx={joinNowTitleSx}>
                    <Link to="/myaccount/overview">Join Now</Link>
                  </Typography>
                  <Divider sx={sectionDividerSx} />
                </Grid>
              )}
            </Grid>

            {/* Nested under the parent /myaccount/* route (Routes.js), so
                paths here are relative; redirect targets stay absolute to
                leave no doubt where they land. Deeper URL space (e.g.
                overview/marketing-prefs) needs the trailing splats. */}
            <Routes>
              <Route
                index
                element={<Navigate to="/myaccount/overview" replace />}
              />
              <Route path="overview/*" element={<Overview />} />
              {/* Handle Auto Renew  */}
              <Route path="autorenew-off/*" element={<AutorenewOff />} />
              <Route path="autorenew-on/*" element={<AutorenewOn />} />
              {/* Standard Profile Updates */}
              <Route path="editprofile/*" element={<EditProfile />} />
              <Route path="change-name/*" element={<ChangeName />} />
              <Route path="change-email/*" element={<ChangeEmail />} />
              <Route path="change-password/*" element={<ChangePassword />} />
              <Route path="add-phone/*" element={<AddPhone />} />
              <Route path="change-phone/*" element={<ChangePhone />} />
              <Route path="payment-history/*" element={<PaymentHistory />} />
              <Route
                path="payment-method/:action/*"
                element={<PaymentMethod />}
              />

              {/* Payment Method Redirects */}
              <Route
                path="add-payment-method"
                element={
                  <Navigate to="/myaccount/payment-method/add" replace />
                }
              />
              <Route
                path="change-payment-method"
                element={
                  <Navigate to="/myaccount/payment-method/update" replace />
                }
              />
              <Route
                path="remove-payment-method"
                element={
                  <Navigate to="/myaccount/payment-method/remove" replace />
                }
              />

              <Route
                path="add-payment-method-autorenew/*"
                element={<AddPaymentMethodAutoRenew />}
              />
              {/* Upgrade / Join Now */}
              <Route path="join-now/*" element={<JoinNow />} />
              {/* Annual Upgrade */}
              <Route path="annual-upgrade/*" element={<AnnualUpgrade />} />
            </Routes>
          </>
        </Container>
      </PageBackground>
      <Footer />
    </>
  );
}
